import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
    "Advanced Manufacturing",
    "Data Center",
    "Healthcare",
    "Education",
    "Highrise / Residential",
    "Commercial",
    "Master Planning",
    "Other",
];

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        project_type: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in name, email and message.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form };
            Object.keys(payload).forEach((k) => {
                if (!payload[k]) delete payload[k];
            });
            await axios.post(`${API}/contact`, payload);
            toast.success("Thanks — our team will reach out within 1 business day.");
            setForm({ name: "", email: "", company: "", phone: "", project_type: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full bg-slate-900/60 border border-slate-800 focus:border-blue-500 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 transition-colors duration-200 outline-none text-sm";

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-slate-400 mb-2">Name *</label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Jane Doe"
                        className={inputCls}
                        required
                        data-testid="contact-name"
                    />
                </div>
                <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-slate-400 mb-2">Email *</label>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="jane@company.com"
                        className={inputCls}
                        required
                        data-testid="contact-email"
                    />
                </div>
                <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-slate-400 mb-2">Company</label>
                    <input
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Acme Corp"
                        className={inputCls}
                        data-testid="contact-company"
                    />
                </div>
                <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-slate-400 mb-2">Phone</label>
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="+1 (555) 000-0000"
                        className={inputCls}
                        data-testid="contact-phone"
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-slate-400 mb-2">Project Type</label>
                <select
                    value={form.project_type}
                    onChange={update("project_type")}
                    className={inputCls}
                    data-testid="contact-project-type"
                >
                    <option value="" className="bg-slate-900">Select a sector</option>
                    {projectTypes.map((p) => (
                        <option key={p} value={p} className="bg-slate-900">{p}</option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-slate-400 mb-2">Project Details *</label>
                <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Tell us about scope, location, timeline and what you're trying to achieve..."
                    className={inputCls + " resize-none"}
                    required
                    data-testid="contact-message"
                />
            </div>

            <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white font-semibold px-6 py-4 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(37,99,235,0.35)] hover:shadow-[0_0_50px_rgba(37,99,235,0.55)]"
                data-testid="contact-submit"
            >
                {submitting ? (
                    <>
                        <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                    </>
                ) : (
                    <>
                        Send Message <Send className="w-4 h-4" />
                    </>
                )}
            </button>

            <p className="text-xs text-slate-500 text-center">
                We typically respond within 1 business day. No spam — ever.
            </p>
        </form>
    );
}
